/**
 * PDF-Lib Loader - Conditional Import System
 * ==========================================
 * Handles pdf-lib imports for both development (ES6 modules) and production (CDN)
 */

// Check if we're in development mode (Vite dev server)
const isDevelopment =
  (import.meta as any)?.env?.DEV ||
  (typeof window !== "undefined" &&
    (window.location?.port === "3000" || window.location?.port === "3001"));

// Type definitions for pdf-lib
interface PDFLib {
  PDFDocument: any;
  rgb: any;
  StandardFonts: any;
  [key: string]: any;
}

// Global variable to store pdf-lib instance
let pdfLibInstance: PDFLib | null = null;

const trace = false;

/**
 * Load pdf-lib based on environment
 */
export async function loadPDFLib(): Promise<PDFLib> {
  if (pdfLibInstance) {
    return pdfLibInstance;
  }

  // In development mode, always use CDN to avoid font loading issues
  if (isDevelopment) {
    console.log("🔧 Development mode detected, using CDN globals");

    try {
      // Attendre que le CDN soit chargé
      let attempts = 0;
      const maxAttempts = 100; // Augmenter le nombre de tentatives

      while (!window.PDFLib && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;

        // Log de progression
        if (attempts % 10 === 0) {
          console.log(`⏳ Attente CDN pdf-lib... (${attempts}/${maxAttempts})`);
        }
      }

      if (!window.PDFLib) {
        throw new Error(
          `PDFLib not available on window object after ${maxAttempts * 100}ms`
        );
      }

      // Vérifier que les composants nécessaires sont disponibles
      if (!window.PDFLib.PDFDocument) {
        throw new Error("PDFDocument not available in PDFLib");
      }
      if (!window.PDFLib.StandardFonts) {
        throw new Error("StandardFonts not available in PDFLib");
      }

      pdfLibInstance = window.PDFLib;
      if (trace) {
        console.log("✅ Successfully loaded pdf-lib via CDN globals");
        console.log("📊 PDFDocument:", typeof window.PDFLib.PDFDocument);
        console.log("📊 StandardFonts:", typeof window.PDFLib.StandardFonts);
      }
      return pdfLibInstance;
    } catch (cdnError) {
      console.error("❌ Failed to load pdf-lib via CDN:", cdnError);
      throw new Error(
        `pdf-lib not available via CDN: ${(cdnError as Error).message}`
      );
    }
  }

  // In production, try ES6 imports first, then fallback to CDN
  try {
    if (trace) {
      console.log("🔧 Attempting to load pdf-lib via ES6 imports");
    }
    const { PDFDocument, rgb, StandardFonts } = await import("pdf-lib");
    pdfLibInstance = { PDFDocument, rgb, StandardFonts };
    if (trace) {
      console.log("✅ Successfully loaded pdf-lib via ES6 imports");
    }
    return pdfLibInstance;
  } catch (es6Error) {
    if (trace) {
      console.log("⚠️ ES6 imports failed, trying CDN globals...");
    }

    // Fallback to CDN globals
    try {
      let attempts = 0;
      const maxAttempts = 50;

      while (!window.PDFLib && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (!window.PDFLib) {
        throw new Error(
          "PDFLib not available on window object after 5 seconds"
        );
      }

      pdfLibInstance = window.PDFLib;
      if (trace) {
        console.log("✅ Successfully loaded pdf-lib via CDN globals");
      }
      return pdfLibInstance;
    } catch (cdnError) {
      console.error("❌ Failed to load pdf-lib via both ES6 and CDN:", {
        es6Error,
        cdnError,
      });
      throw new Error(
        `pdf-lib not available: ES6 error: ${
          (es6Error as Error).message
        }, CDN error: ${(cdnError as Error).message}`
      );
    }
  }
}

/**
 * Get PDFDocument from loaded pdf-lib
 */
export async function getPDFDocument() {
  const pdfLib = await loadPDFLib();
  return pdfLib.PDFDocument;
}

/**
 * Get rgb function from loaded pdf-lib
 */
export async function getRGB() {
  const pdfLib = await loadPDFLib();
  return pdfLib.rgb;
}

/**
 * Get StandardFonts from loaded pdf-lib
 */
export async function getStandardFonts() {
  const pdfLib = await loadPDFLib();
  return pdfLib.StandardFonts;
}

// Note: PDFLib global declaration is handled elsewhere in the project
